import React, { useState, useEffect, useRef } from 'react';
import { FaPlay } from 'react-icons/fa';
import TextEditor from '../../texteditor/TextEditor';
import styles from './styles/ScenarioTextEditorModal.module.css';

const AUTOSAVE_DELAY_MS = 3000;
const MIN_SAVE_INDICATOR_MS = 500;

const ScenarioTextEditorModal = ({ onClose, initialText, onSave, onPreview, situationTitle }) => {
    const [saveStatus, setSaveStatus] = useState('saved');
    const autosaveTimerRef = useRef(null);
    const isDirtyRef = useRef(false);
    const latestTextRef = useRef(initialText || '');
    const editorRef = useRef(null);
    const docxInputRef = useRef(null);

    /**
     * Imports a Word document at the cursor through the editor's importWord
     * command; the resulting change autosaves like any other edit. Progress
     * and problems are shown by the editor itself.
     */
    const handleDocxImport = async (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        const editor = editorRef.current;
        if (!file || !editor || editor.state === 'destroyed') return;
        try {
            await editor.execute('importWord', { file });
        } catch (err) {
            // Already reported in the editor's import banner.
        }
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirtyRef.current) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        };
    }, []);

    const executeSave = async (textToSave) => {
        setSaveStatus('saving');
        const start = Date.now();
        try {
            await onSave(textToSave);
            const remaining = MIN_SAVE_INDICATOR_MS - (Date.now() - start);
            if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
            setSaveStatus('saved');
            isDirtyRef.current = false;
        } catch (err) {
            console.error('Autosave failed:', err);
            setSaveStatus('unsaved');
        }
    };

    const handleChange = (data) => {
        latestTextRef.current = data;
        setSaveStatus('unsaved');
        isDirtyRef.current = true;
        if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            executeSave(data);
        }, AUTOSAVE_DELAY_MS);
    };

    const handleSaveAndClose = async () => {
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        if (isDirtyRef.current) {
            setSaveStatus('saving');
            try {
                await onSave(latestTextRef.current);
            } catch (err) {
                console.error('Save on close failed:', err);
            }
        }
        onClose();
    };

    /**
     * Hands the latest text to the caller rather than saving it here: the
     * caller commits only what differs from the draft and flushes before the
     * preview loads, so saving first as well would send the same text twice.
     */
    const handlePreview = () => {
        if (autosaveTimerRef.current) {
            clearTimeout(autosaveTimerRef.current);
            autosaveTimerRef.current = null;
        }
        isDirtyRef.current = false;
        onPreview(latestTextRef.current);
    };

    const statusText =
        saveStatus === 'saving' ? 'Saving...' :
        saveStatus === 'unsaved' ? 'Unsaved changes' :
        'Saved';

    const statusClass =
        saveStatus === 'saving' ? styles.statusSaving :
        saveStatus === 'unsaved' ? styles.statusUnsaved :
        styles.statusSaved;

    return (
        <div className={styles.overlay}>
            <div className={styles.header}>
                <span className={styles.situationTitle}>{situationTitle || 'Edit Text'}</span>
                <div className={styles.headerRight}>
                    <input
                        type="file"
                        accept=".docx,.docm,.dotx,.dotm"
                        style={{ display: 'none' }}
                        ref={docxInputRef}
                        onChange={handleDocxImport}
                    />
                    <button
                        className={styles.importDocxButton}
                        onClick={() => docxInputRef.current.click()}
                    >
                        Import Word Doc
                    </button>
                    {onPreview && (
                        <button
                            className={`${styles.importDocxButton} ${styles.previewButton}`}
                            onClick={handlePreview}
                            disabled={saveStatus === 'saving'}
                        >
                            <FaPlay /> Preview
                        </button>
                    )}
                    <span className={statusClass}>{statusText}</span>
                    <button
                        className={styles.saveCloseButton}
                        onClick={handleSaveAndClose}
                        disabled={saveStatus === 'saving'}
                    >
                        Save & Close
                    </button>
                </div>
            </div>
            <div className={styles.editorContainer}>
                <TextEditor
                    initialData={initialText || ''}
                    onChange={handleChange}
                    editorRef={editorRef}
                />
            </div>
        </div>
    );
};

export default ScenarioTextEditorModal;
